import { format, isValid, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import useAuth from "$hooks/useAuth";
import useEDBIntegratedOrders from "$hooks/useEDBIntegratedOrders";
import { appState } from "$reducers";
import { EdbStatus } from "$types";
import { getFromLS } from "$utils/utils";

interface UseEDBFormLogicProps {
  idEdb: string | null;
  currentEdb: any;
}

const formatEdbDate = (date?: string | null) => {
  if (!date) return null;
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : null;
};

export const useEDBFormLogic = ({
  idEdb,
  currentEdb,
}: UseEDBFormLogicProps) => {
  const { filter: sidebarCompanyFilter } = useSelector(appState);
  const { isAdmin } = useAuth();
  const [openDeleteEDBModal, setOpenDeleteEDBModal] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [dockyardOptions, setDockyardOptions] = useState([]);

  const defaultValues = useMemo(
    () => ({
      _id: idEdb ?? null,
      project: currentEdb?.project?._id ?? null,
      dockyard: currentEdb?.dockyard?._id ?? null,
      company: sidebarCompanyFilter?._id ?? currentEdb?.company?._id,
      dispositionAdress: currentEdb?.dispositionAdress ?? "",
      dispositionAdress2: currentEdb?.dispositionAdress2 ?? "",
      dispositionCity: currentEdb?.dispositionCity ?? "",
      dispositionCountry: currentEdb?.dispositionCountry ?? "France",
      dispositionPlace: currentEdb?.dispositionPlace ?? "",
      dispositionDateIsExact: currentEdb?.dispositionDateIsExact ?? false,
      dispositionPostalCode: currentEdb?.dispositionPostalCode ?? "",
      dispositionType: currentEdb?.dispositionType ?? "",
      emailContactRecovery: currentEdb?.emailContactRecovery ?? "",
      entrepotName: currentEdb?.entrepotName ?? "",
      firstnameContactRecovery: currentEdb?.firstnameContactRecovery ?? "",
      lastnameContactRecovery: currentEdb?.lastnameContactRecovery ?? "",
      phoneContactRecovery: currentEdb?.phoneContactRecovery ?? "",
      preparationType:
        currentEdb?.preparationType ?? "Préparation pour mise à disposition",
      analyticCode: currentEdb?.analyticCode ?? "",
      intervenantCompany: currentEdb?.intervenantCompany ?? null,
      intervenantContact: currentEdb?.intervenantContact ?? null,
      integratedOrders: null,
      dispositionDate: formatEdbDate(currentEdb?.dispositionDate),
      scheduledInterventionDate: formatEdbDate(
        currentEdb?.scheduledInterventionDate
      ),
      products:
        currentEdb?.products?.map((p) => {
          if (p.relatedProduct.isReferenced) {
            return {
              productID: p.relatedProduct._id,
              qty: p.qty,
            };
          }
          return {
            ...p.relatedProduct,
            productID: p.relatedProduct._id,
            qty: p.qty,
          };
        }) ?? [],
    }),
    [idEdb, currentEdb, sidebarCompanyFilter]
  );

  // Handle localStorage dockyard data
  const [sourceDockyardCompany, setSourceDockyardCompany] = useState<{
    _id: string;
    name: string;
  } | null>(null);

  const dock: any = getFromLS("dockyard");
  localStorage.removeItem("dockyard");
  if (dock) {
    const pr: any = JSON.parse(dock);
    defaultValues.project = pr.project._id;
    defaultValues.dockyard = pr._id;
    defaultValues.company = pr.company._id;
    defaultValues.intervenantCompany = pr.interveningCompany?.[0]?._id;
    if (!sourceDockyardCompany && pr.company) {
      setTimeout(() => {
        setSourceDockyardCompany({
          _id: pr.company._id,
          name: pr.company.name,
        });
      }, 0);
    }
  }

  const formMethods = useForm({
    mode: "onChange",
    defaultValues,
  });

  const { integratedOrders, isLoadingIntegratedOrders } =
    useEDBIntegratedOrders(currentEdb?._id);

  const [showCart, setShowCart] = useState(
    !!(
      currentEdb?.status === EdbStatus.CART_CREATED &&
      (currentEdb?.products?.length > 0 ||
        currentEdb?.nbIntegratedOrderProducts > 0)
    )
  );

  useEffect(() => {
    formMethods.reset({ ...formMethods.getValues(), integratedOrders });
  }, [integratedOrders, formMethods]);

  const [step, setStep] = useState(
    currentEdb?.status === EdbStatus.CART_CREATED &&
      (currentEdb?.products?.length > 0 ||
        currentEdb?.nbIntegratedOrderProducts > 0)
      ? 1
      : 0
  );

  const currentCompany = formMethods.watch("company", defaultValues.company);
  const currentProject = formMethods.watch("project", defaultValues.project);
  const currentDockyard = formMethods.watch("dockyard", defaultValues.dockyard);
  const cart = formMethods.watch("products", defaultValues.products);
  const orderProducts = formMethods.watch("integratedOrders.orderProducts");

  // We always allow navigating across all steps (Identification -> Besoin matériel -> Préparation et livraison).
  // Step content itself can still guide/disable actions if some information is missing (e.g. company).
  const maxStepIndex = 2;

  const changeStep = useCallback(
    (stepNumber: number) => {
      if (stepNumber <= maxStepIndex) {
        setStep(stepNumber);
      }
    },
    [maxStepIndex]
  );

  /**
   * Reset form if changing company as an admin
   * See: https://gitlab.com/tarikbaba/batail-log-frontend/-/merge_requests/1045
   */
  useEffect(() => {
    if (
      sidebarCompanyFilter?._id &&
      sidebarCompanyFilter._id !== currentCompany &&
      isAdmin()
    ) {
      formMethods.reset();
      setStep(0);
    }
  }, [currentCompany, isAdmin, formMethods, sidebarCompanyFilter]);

  return {
    openDeleteEDBModal,
    setOpenDeleteEDBModal,
    projectOptions,
    setProjectOptions,
    dockyardOptions,
    setDockyardOptions,
    defaultValues,
    integratedOrders,
    isLoadingIntegratedOrders,
    showCart,
    setShowCart,
    step,
    setStep,
    maxStepIndex,
    changeStep,
    currentCompany,
    currentProject,
    currentDockyard,
    cart,
    orderProducts,
    sidebarCompanyFilter,
    sourceDockyardCompany,
    ...formMethods,
    errors: formMethods.formState.errors,
  };
};
